import { ReasonInput } from '@strata/react-compound-inputs';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { updatePlaceOfService } from 'api/treatmentAPI';
import { handleError } from 'services/Error';
import { useGetPlacesOfServiceQuery } from 'store/api/referenceApi';

const Title = 'Update Place Of Service';

const ReasonOptions = [
    'Renewal auth approved for POS change',
    'Prescriber will B&B',
    'Incorrect POS approved/UM change made',
];

export const UpdatePlaceOfServiceDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
    submitCallback,
}) => {
    const { payerId, treatmentId, placeOfService } = treatmentDetails ?? {};
    const { data: placesOfService, isFetching: placesOfServiceFetching } =
        useGetPlacesOfServiceQuery();
    const placeOfServiceOptions = placesOfService?.filter(
        (place) => place.value !== placeOfService,
    );

    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                placeOfService: Yup.string().required(validationStrings.requiredField.errorMessage),
                ...ReasonInput.validationSchema,
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await updatePlaceOfService({
                        payerId,
                        treatmentId,
                        placeOfService: values.placeOfService,
                        ...ReasonInput.submitValues(values),
                    });

                    submitCallback();

                    SuccessToast({ message: 'Place of service updated successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update place of service. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update Place Of Service"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="placeOfService"
                                label="Place Of Service"
                                required
                                isLoading={placesOfServiceFetching}
                                values={placeOfServiceOptions}
                                onChange={(newValue) =>
                                    formik.setFieldValue('placeOfService', newValue?.value)
                                }
                                selectedItem={placeOfServiceOptions?.find(
                                    ({ value }) => value === formik.values.placeOfService,
                                )}
                            />
                            <ReasonInput reasons={ReasonOptions} />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

UpdatePlaceOfServiceDialog.Title = Title;

UpdatePlaceOfServiceDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        payerId: PropTypes.string.isRequired,
        productServiceOid: PropTypes.string.isRequired,
        placeOfService: PropTypes.string,
        isBrand: PropTypes.bool,
        benefitType: PropTypes.string,
        activeReferral: PropTypes.shape({
            servicingSupplierNpi: PropTypes.string,
        }),
        memberDetails: PropTypes.shape({
            memberId: PropTypes.string,
            firstName: PropTypes.string,
            memberState: PropTypes.string,
            dateOfBirth: PropTypes.string,
            lineOfBusiness: PropTypes.string,
            lobSegment: PropTypes.string,
        }),
    }),
    submitCallback: PropTypes.func.isRequired,
};
